import React, { useCallback } from 'react';
import { GestureResponderEvent } from 'react-native';
import { useActor } from '@xstate/react';
import { DNABox, DNAButton } from '@alucio/lux-ui';
import { DNAChip } from 'src/components/DNA';
import { MeetingORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { CRMConnectionStatus } from 'src/state/machines/CRM/crmMachineTypes';
import { useUserTenant } from 'src/state/redux/selector/user';
import { useDispatch } from 'src/state/redux';
import { drawerActions, DRAWER_ENTITIES } from 'src/state/redux/slice/drawer';
import { getCrmDisplayStatus } from './utils/getCrmDisplayStatus';
import { CRMUtil } from 'src/state/machines/CRM/util';
import { MEETING_SUBMIT_TYPE } from './AddMeetingProvider';

interface CrmStatusCellProps {
  meetingORM: MeetingORM;
}

const CrmStatusCell: React.FC<CrmStatusCellProps> = ({ meetingORM }) => {
  const { isOnline, crmMachine } = useAppSettings();
  const { connectionStatus, isCRMSyncing } = useCRMStatus();
  const currentTenant = useUserTenant();
  const dispatch = useDispatch();
  const [, send] = useActor(crmMachine);

  const { variant, showSyncButton } = getCrmDisplayStatus(meetingORM);
  const isConnectedToCRM = connectionStatus === CRMConnectionStatus.CONNECTED;

  const handleSyncNowPress = useCallback(
    (e: GestureResponderEvent) => {
      e.stopPropagation();

      if (!isConnectedToCRM && !isCRMSyncing) {
        CRMUtil.setPostAuthIntent({
          type: 'MEETING_SUBMIT',
          meetingId: meetingORM.model.id,
          submitType: MEETING_SUBMIT_TYPE.SAVE_TO_CRM,
        });
        send({
          type: 'LOGIN_CRM',
          payload: currentTenant?.config.crmIntegration,
          options: {
            returnPath: '/meetings',
            trigger: 'SAVE_TO_CRM',
          },
        });
        return;
      }

      dispatch(drawerActions.toggle({
        entity: DRAWER_ENTITIES.MEETING,
        entityId: meetingORM.model.id,
        autoSubmitToCRM: true,
      }));
    },
    [isConnectedToCRM, isCRMSyncing, currentTenant, meetingORM.model.id, dispatch, send],
  );

  return (
    <DNABox appearance="col" alignX="start">
      <DNABox appearance="col" spacing="sm" alignX="start">
        <DNAChip.Beacon testID="crm-status-badge" variant={variant} />
        {showSyncButton && (
          <DNAButton
            testID="crm-sync-now-button"
            appearance="filled"
            status="warning"
            size="sm"
            onPress={handleSyncNowPress}
            disabled={!isOnline}
            style={{ minWidth: 115 }}
          >
            Sync now
          </DNAButton>
        )}
      </DNABox>
    </DNABox>
  );
};

export default CrmStatusCell;
